import { memo, useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { BarElement, ChartsTooltipContainer, SparkLineChart, useAxesTooltip } from '@mui/x-charts';
import {
  getRiskIndicatorChart,
  getRiskIndicatorTooltipLabel,
  getRiskIndicatorTooltipValue,
} from '../utils/riskIndicators';

export function CustomAxisTooltip({ riskIndicatorKey, metadata }) {
  const tooltipData = useAxesTooltip();

  const firstAxisData = tooltipData?.[0];

  if (!firstAxisData) {
    return null;
  }

  const { axisFormattedValue, seriesItems, dataIndex } = firstAxisData;

  return (
    <Paper
      elevation={0}
      sx={{
        m: 1,
        boxShadow:
          'rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, ' +
          'rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, ' +
          'rgba(0, 0, 0, 0.12) 0px 1px 3px 0px',
        table: { borderSpacing: 0 },
        thead: {
          td: {
            px: 2,
            py: 1,
            borderBottomStyle: 'solid',
            borderBottomWidth: 1,
            borderBottomColor: 'divider',
          },
        },
        tbody: {
          'tr:first-of-type': { td: { paddingTop: 1 } },
          'tr:last-of-type': { td: { paddingBottom: 1 } },
          tr: {
            'td:first-of-type': { paddingLeft: 2 },
            'td:last-of-type': { paddingLeft: 4, paddingRight: 2 },
            td: { paddingRight: 1, paddingBottom: 1 },
          },
        },
      }}
    >
      <table>
        <thead>
          <tr>
            <td colSpan={3}>
              <Typography color="rgba(0, 0, 0, 0.6)">{axisFormattedValue}</Typography>
            </td>
          </tr>
        </thead>
        <tbody>
          {seriesItems.map(seriesItem => (
            <tr key={seriesItem.seriesId}>
              <td aria-label={`${seriesItem.formattedLabel}-series-color`}>
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 2,
                    backgroundColor: seriesItem.color,
                  }}
                />
              </td>
              <td>
                <Typography color="rgba(0, 0, 0, 0.6)">
                  {getRiskIndicatorTooltipLabel(riskIndicatorKey, dataIndex, metadata)}
                </Typography>
              </td>
              <td>
                <Typography color="rgba(0, 0, 0, 0.87)">
                  {seriesItem.formattedValue === null
                    ? ''
                    : getRiskIndicatorTooltipValue(riskIndicatorKey, seriesItem.formattedValue)}
                </Typography>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Paper>
  );
}

const CustomTooltip = memo(({ riskIndicatorKey, metadata }) => (
  <ChartsTooltipContainer>
    <CustomAxisTooltip riskIndicatorKey={riskIndicatorKey} metadata={metadata} />
  </ChartsTooltipContainer>
));

function RiskIndicatorChart({ riskIndicatorKey, riskIndicator }) {
  const { xAxisData, metadata, data } = useMemo(
    () => getRiskIndicatorChart(riskIndicatorKey, riskIndicator),
    [riskIndicatorKey, riskIndicator],
  );

  const containerHeight = 36;
  const containerWidth = 140;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <SparkLineChart
        plotType="bar"
        xAxis={{ scaleType: 'band', data: xAxisData }}
        slots={{
          // eslint-disable-next-line react/no-unstable-nested-components
          bar: props => (
            <BarElement
              {...props}
              height={Math.max(0.5, props.height)}
              y={props.height <= 0.5 ? containerHeight - 5 : props.y}
            />
          ),
          // eslint-disable-next-line react/no-unstable-nested-components
          tooltip: () => <CustomTooltip riskIndicatorKey={riskIndicatorKey} metadata={metadata} />,
        }}
        data={data}
        height={containerHeight}
        width={containerWidth}
        showTooltip
        showHighlight
      />
    </Box>
  );
}

export default memo(RiskIndicatorChart);
